import React from "react";
import { LocaleProvider } from "@insightfulscience/smart-react/i18";
import WithConditions from "@insightfulscience/smart-react/hocs/WithConditions";
import { createAndRaise } from "@insightfulscience/shared-utils/fn/create-and-raise";
import ApplicationError from "@insightfulscience/smart-react/ApplicationError";
import pipe from "@insightfulscience/shared-utils/fn/pipe";

import { createClient, createCacheKey } from "../../client";
import UpdatesPage from "./Page";
import { BRAND_CODE_GN, PUBLISHED, RELEASE_NOTES_PER_PAGE } from "../../constants";
import { getQueryPage, paginationConfig, paginationInit } from "../../utils/pagination";
import locales from "../../locales";

const normalizeFields = data => ({
	slug: data.slug || "",
	title: data.title || "",
	version: data.appVersionNumber || "",
	majorVersion: data.appVersionNumber.split(".")[0] || "",
	windowsInstallerUrl: data.windowsInstallerUrl || "",
	macInstallerUrl: data.macInstallerUrl || "",
	redHatInstallerUrl: data.redHatInstallerUrl || "",
	ubuntuInstallerUrl: data.ubuntuInstallerUrl || "",
	linuxInstallerUrl: data.linuxInstallerUrl || "",
});

const ContentfulUpdatesFactory = ({ Layout, contentfulProps, pageProps }) => {
	const { brandCode } = pageProps;

	const ContentfulUpdatesPage = ({ page }) => {
		const { resources = null } = pageProps;

		const PageComponent = pageProps.PageComponent || UpdatesPage;

		return (
			<Layout>
				<LocaleProvider resources={resources || locales[brandCode]}>
					<PageComponent page={page} pageProps={pageProps} />
				</LocaleProvider>
			</Layout>
		);
	};

	ContentfulUpdatesPage.getInitialProps = async ({ query }) => {
		const page = getQueryPage(query);
		const select = query.select || "";
		const client = createClient(contentfulProps);

		let latestVersionQuery = {
			content_type: "pageReleaseNotes",
			order: "-fields.releaseDate",
			"fields.publishStatus": PUBLISHED,
			skip: 0,
			limit: 1,
		};

		if (brandCode === BRAND_CODE_GN) {
			delete latestVersionQuery.limit;
			latestVersionQuery = {
				...latestVersionQuery,
				"fields.slug[match]": "prime-",
			};
		}

		const latestVersionData = await client.getEntries(
			latestVersionQuery,
			createCacheKey({ t: "list", ct: "release-note", f: latestVersionQuery }),
		);

		const latestVersion = latestVersionData?.items[0]?.fields
			? normalizeFields(latestVersionData?.items[0]?.fields)
			: {};

		const previousVersions =
			brandCode === BRAND_CODE_GN && latestVersionData?.items?.length
				? latestVersionData?.items.slice(1).map(el => normalizeFields(el?.fields))
				: [{}];

		const contentfulResponse = await client.getEntries(
			{
				content_type: "pageReleaseNotes",
				order: "-fields.releaseDate",
				"fields.publishStatus": PUBLISHED,
				...(select ? { "fields.slug[match]": select } : {}),
				...paginationInit(RELEASE_NOTES_PER_PAGE, page),
			},
			createCacheKey({ t: "list", ct: "release-note", p: page, f: select }),
		);

		if (contentfulResponse.total === 0) {
			return {
				page: null,
			};
		}

		return {
			page: {
				latestVersion,
				previousVersions,
				releaseNotes: contentfulResponse.items,
				...paginationConfig(contentfulResponse.total, RELEASE_NOTES_PER_PAGE, page),
			},
		};
	};

	ContentfulUpdatesPage.propTypes = {
		...UpdatesPage.propTypes,
	};

	ContentfulUpdatesPage.postConditions = [
		[({ page }) => page, createAndRaise(ApplicationError.NotFound)],
	];

	return pipe(ContentfulUpdatesPage, WithConditions);
};

export default ContentfulUpdatesFactory;
